import React, { useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, LabelList, ResponsiveContainer,
} from 'recharts';
import { AnalyticsPeriod, PeriodKey } from '../../../../hooks/useBoardAnalytics';
import DateRangePills from './DateRangePills';

interface FunnelChartProps {
  periods: Record<PeriodKey, AnalyticsPeriod>;
  showValues?: boolean;
}

const BAR_COLOR = '#374151';

function CustomTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px', fontSize: 13 }}>
      <div style={{ fontWeight: 600, color: '#111827', marginBottom: 4 }}>{d.swimlane_name}</div>
      <div style={{ color: '#6b7280' }}>Jobs reached: <b style={{ color: '#111827' }}>{d.count}</b></div>
      <div style={{ color: '#6b7280' }}>Conversion: <b style={{ color: '#111827' }}>{d.conversion}%</b></div>
    </div>
  );
}

export default function FunnelChart({ periods, showValues = true }: FunnelChartProps) {
  const [period, setPeriod] = useState<PeriodKey>('all_time');
  const data = periods[period]?.funnel ?? [];

  // Absolute count + cumulative % of the first stage, drawn just past each bar.
  const renderValueLabel = (props: any) => {
    const { x, y, width, height, index } = props;
    const d = data[index];
    if (!d) return null;
    return (
      <text x={x + width + 8} y={y + height / 2} dy={4} fontSize={12} textAnchor="start">
        <tspan fill="#111827" fontWeight={600}>{d.count}</tspan>
        <tspan fill="#9ca3af" fontWeight={400}> ({d.conversion}%)</tspan>
      </text>
    );
  };

  return (
    <div className="metrics-card">
      <div className="metrics-card__header">
        <span className="metrics-card__title">Job Search Funnel</span>
        <DateRangePills value={period} onChange={setPeriod} />
      </div>
      {data.length === 0 ? (
        <div className="metrics-empty">No data yet</div>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          {/* extra right margin + axis headroom so the value labels never clip */}
          <BarChart data={data} layout="vertical" margin={{ top: 0, right: showValues ? 80 : 40, left: 10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f3f4f6" />
            <XAxis
              type="number"
              domain={[0, (max: number) => Math.ceil(max * 1.25)]}
              tick={{ fontSize: 12, fill: '#9ca3af' }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              type="category"
              dataKey="swimlane_name"
              tick={{ fontSize: 12, fill: '#374151' }}
              axisLine={false}
              tickLine={false}
              width={90}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f9fafb' }} />
            <Bar dataKey="count" radius={[0, 4, 4, 0]}>
              {data.map((_, idx) => (
                <Cell key={idx} fill={BAR_COLOR} opacity={1 - idx * 0.12} />
              ))}
              {showValues && <LabelList dataKey="count" content={renderValueLabel} />}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
